// 2026-10-06：列表跳转详情前保存本历史条目的页码、筛选与浏览位置，返回时按店铺和账号恢复。
import { isRef, nextTick, onUnmounted, unref } from 'vue'

type Fields = Record<string, object>
interface Snapshot {
  // 2026-10-15：历史列表同时核对权限指纹，保留既有导航来源的机构/客户scope。
  context?: string
  scope: string
  path: string
  fields: Record<string, unknown>
  scrollTop: number
}
const stateKey = 'landiyunCatalogReturn'
const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value))
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
const matchesShape = (value: unknown, template: unknown): boolean => {
  if (template === undefined || template === null) return true
  if (Array.isArray(template)) return Array.isArray(value)
  if (isRecord(template)) return isRecord(value) &&
    Object.entries(template).every(([key, item]) => matchesShape(value[key], item))
  return typeof value === typeof template
}

export function createCatalogHistoryState(fields: Fields, scope: () => string, context?: () => string) {
  const path = window.location.pathname
  const initialScope = scope()
  let restored: Snapshot | undefined
  let disposed = false
  onUnmounted(() => { disposed = true })

  const remember = (): void => {
    if (scope() !== initialScope || window.location.pathname !== path) return
    try {
      const snapshot: Snapshot = {
        scope: scope(), path, context: context?.(),
        fields: Object.fromEntries(Object.entries(fields).map(([key, field]) => [key, clone(unref(field))])),
        scrollTop: document.querySelector('.app-main')?.scrollTop || 0
      }
      // 保留Vue Router的back/current/forward/position，不增加历史记录或改变地址。
      window.history.replaceState({ ...window.history.state, [stateKey]: snapshot }, '')
    } catch (_error) {
      // 浏览器禁止写入历史状态时仍允许原来的商品详情跳转。
    }
  }

  const restore = (): boolean => {
    try {
      const saved = window.history.state?.[stateKey] as Snapshot | undefined
      if (!saved || (context && saved.context !== context()) || scope() !== initialScope || saved.scope !== initialScope || saved.path !== path || !isRecord(saved.fields)) return false
      const data = clone(saved.fields)
      if (!Number.isInteger(data.current) || Number(data.current) < 1 ||
          !Number.isInteger(data.pageSize) || Number(data.pageSize) < 1) return false
      // 仅恢复本页面声明的字段，拒绝旧格式或损坏数据，避免跨商品类别套用筛选。
      for (const [key, field] of Object.entries(fields)) {
        const value = unref(field)
        if (!(key in data) || !matchesShape(data[key], value)) return false
      }
      for (const [key, field] of Object.entries(fields)) {
        if (isRef(field)) field.value = data[key]
        else if (isRecord(field) && isRecord(data[key])) Object.assign(field, data[key])
      }
      restored = { ...saved, fields: data }
      return true
    } catch (_error) {
      return false
    }
  }

  const restoreScroll = async (): Promise<void> => {
    if (!restored) return
    await nextTick()
    await new Promise<void>(resolve => window.requestAnimationFrame(() => resolve()))
    if (disposed || window.location.pathname !== path || restored.scope !== scope()) return
    const container = document.querySelector('.app-main')
    if (container && Number.isFinite(restored.scrollTop)) container.scrollTop = Math.max(0, restored.scrollTop)
    restored = undefined
  }
  return { remember, restore, restoreScroll }
}
